import { API_ENDPOINTS } from '../config/api'
import { safeJson } from '../lib/utils'

type ApiResult<T> = { success: boolean; data?: T; message?: string }

async function apiFetch<T>(url: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  })
  const body = (await safeJson(res)) as ApiResult<T> & { message?: string }
  if (!res.ok) throw new Error(body?.message || `Request failed (${res.status})`)
  if (body && typeof body === 'object' && 'success' in body && body.success === false) {
    throw new Error(body.message || 'Request failed')
  }
  return (body?.data ?? body) as T
}

function orgQuery(orgId?: string | null, extra?: Record<string, string | undefined>) {
  const p = new URLSearchParams()
  if (orgId) p.set('organization_id', orgId)
  if (extra) {
    for (const [k, v] of Object.entries(extra)) {
      if (v != null && v !== '') p.set(k, v)
    }
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

export type TopVulnerability = {
  id: string
  fingerprint?: string
  cve: string | null
  title: string
  severity: string
  cvssScore: number | null
  isExploitable: boolean | null
  internetExposed?: boolean
  affectedAssetsCount: number
  affectedAssetNames?: string[]
  detectedAt?: string | null
  priorityScore: number
  explanation?: string
}

export type TopFailedControl = {
  id: string
  standardCode: string
  controlId: string
  controlTitle: string
  implementationStatus: string
  reasons: string[]
  missingEvidence?: boolean
  failedVerification?: boolean
  priorityScore: number
  explanation?: string
}

export type TopOpenRisk = {
  id: string
  sourceType: string
  sourceId?: string
  title: string
  severity: string | null
  likelihood: number | null
  impact: number | null
  residualRisk: number | null
  remediationStatus: string | null
  priorityScore: number
  explanation?: string
}

export type OperationalSummaryRow = {
  informationSystemId: string
  name: string
  code?: string | null
  description?: string | null
  lifecycleStatus?: string | null
  criticality?: string | null
  environment?: string | null
  ownerLabel?: string | null
  linkedAssetsCount: number
  blockerCount?: number
  applicableStandards: string[]
  complianceScope?: Array<{
    standardCode: string
    selfAssessedPercent: number | null
    verifiedPercent: number | null
    verifiedStatus?: string | null
    verifiedSource?: string | null
    available?: boolean
  }>
  criticalDataCategories: string[]
  readiness: {
    readinessStatus: string
    controlCoveragePercent: number | null
    remediationOpenCount?: number
  }
  topVulnerabilities: TopVulnerability[]
  topFailedControls: TopFailedControl[]
  topOpenRisks: TopOpenRisk[]
  vulnerabilityCount?: number
  severityDistribution?: {
    critical: number
    high: number
    medium: number
    low: number
    info: number
  }
  failedControlsCount?: number
  openRisksCount?: number
  lastScan?: string | null
  indicators?: Array<{ id: string; severity: string; message: string }>
  /** Explainable Fix Priority (list UX only — not official Risk Engine / not TSH). */
  remediationPriority?: {
    score: number
    band: string
    baseBand?: string
    contextScore?: number
    factors: Array<{
      id: string
      role?: string
      weight?: number
      detail: string
      source?: string
      band?: string
    }>
    primaryFactor?: { id: string; detail: string; band?: string; source?: string | null } | null
    contextFactors?: Array<{ id: string; detail: string; weight?: number }>
    escalation?: {
      fromBand: string
      toBand: string
      reason: string
      source?: string
      evidence?: string
    } | null
    problemCount: number
    hasCriticalOrHigh?: boolean
  }
  /** Same TSH formula as Home Total Security Health (not control coverage). */
  securityHealthPercent?: number
  securityHealthStatus?: string | null
  controlCoveragePercent?: number | null
}

export async function fetchOperationalSummaries(token: string, orgId?: string | null) {
  return apiFetch<OperationalSummaryRow[]>(
    `${API_ENDPOINTS.INFORMATION_SYSTEMS_OPERATIONAL_SUMMARIES}${orgQuery(orgId)}`,
    token
  )
}

export async function fetchOperationalSummary(token: string, systemId: string, orgId?: string | null) {
  return apiFetch<OperationalSummaryRow>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_OPERATIONAL_SUMMARY(systemId)}${orgQuery(orgId)}`,
    token
  )
}

export async function fetchVulnerabilityContext(token: string, vulnId: string, orgId?: string | null) {
  return apiFetch<Record<string, unknown>>(
    `${API_ENDPOINTS.ASSET_VULNERABILITY_CONTEXT(vulnId)}${orgQuery(orgId)}`,
    token
  )
}

export async function fetchControlContext(token: string, controlLinkId: string, orgId?: string | null) {
  return apiFetch<Record<string, unknown>>(
    `${API_ENDPOINTS.ASSET_CONTROL_CONTEXT(controlLinkId)}${orgQuery(orgId)}`,
    token
  )
}

export async function fetchRiskContext(
  token: string,
  sourceType: string,
  sourceId: string,
  orgId?: string | null
) {
  return apiFetch<Record<string, unknown>>(
    `${API_ENDPOINTS.ASSET_RISK_CONTEXT(sourceType, sourceId)}${orgQuery(orgId)}`,
    token
  )
}
